import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { BEYOND, BENEFITS, STEPS, MODELS } from "@/data/studio";
import { Eyebrow, H2, Pill, Reveal } from "./ui";

const wrap = "mx-auto max-w-[1480px] px-5 sm:px-8 lg:px-12";

export const Beyond = () => (
  <section data-testid="studio-beyond" className="bg-sink py-24 sm:py-32 lg:py-40">
    <div className={wrap}>
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow>Beyond Generation</Eyebrow></Reveal>
          <Reveal delay={0.05}><H2 className="mt-6">More Than an <span className="text-brand">AI Generator.</span></H2></Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8">
          <p className="font-display text-xl font-semibold text-white sm:text-2xl">Technology creates possibilities.<br /><span className="text-zinc-400">Production turns them into something worth watching.</span></p>
          <p className="mt-5 text-base leading-relaxed text-zinc-400">A finished video requires far more than generating individual clips. Our team manages the complete creative and production process — bringing together concept, story, visuals, voice, branding, editing and sound into one cohesive production.</p>
        </Reveal>
      </div>
      <div className="mt-16 grid border-l border-t border-white/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="studio-beyond-grid">
        {BEYOND.map(([t, d], i) => (
          <Reveal key={t} delay={(i % 3) * 0.06} className="group border-b border-r border-white/10 p-7 transition-colors duration-500 hover:bg-coal sm:p-10">
            <span className="font-mono text-[11px] text-brand">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-10 font-display text-2xl font-bold text-white sm:mt-14">{t}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-zinc-400">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const WhyAi = () => (
  <section data-testid="studio-why" className="border-t border-white/10 bg-sink py-24 sm:py-32 lg:py-40">
    <div className={wrap}>
      <Reveal><Eyebrow>Why AI Video Production</Eyebrow></Reveal>
      <Reveal delay={0.05}><H2 className="mt-6 max-w-4xl">A Different Way to <span className="text-brand">Produce Video.</span></H2></Reveal>
      <div className="mt-16 grid gap-px overflow-hidden rounded border border-white/10 bg-white/10 md:grid-cols-2">
        {BENEFITS.map(([t, d], i) => (
          <div key={t} data-testid={`studio-benefit-${i + 1}`} className="group bg-sink p-8 transition-colors duration-500 hover:bg-coal sm:p-12 lg:p-16">
            <Reveal delay={(i % 2) * 0.08}>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand">Benefit {String(i + 1).padStart(2, "0")}</p>
              <h3 className="mt-10 font-display text-3xl font-extrabold uppercase leading-[0.95] text-white sm:text-5xl">{t}</h3>
              <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-400">{d}</p>
            </Reveal>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  return (
    <section id="process" data-testid="studio-process" className="border-t border-white/10 bg-sink py-24 sm:py-32 lg:py-40">
      <div className={`${wrap} grid gap-14 lg:grid-cols-12`}>
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal><Eyebrow>How It Works</Eyebrow></Reveal>
            <Reveal delay={0.05}><H2 className="mt-6">From Idea to <span className="text-brand">Final Cut.</span></H2></Reveal>
            <Reveal delay={0.1}><p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400 sm:text-lg">A clear, six-stage production workflow — built so you always know where your project stands.</p></Reveal>
          </div>
        </div>
        <div ref={ref} className="relative lg:col-span-6 lg:col-start-7" data-testid="studio-timeline">
          <div className="absolute bottom-0 left-[7px] top-0 w-px bg-white/10" />
          <motion.div style={{ scaleY: scrollYProgress }} className="absolute bottom-0 left-[7px] top-0 w-px origin-top bg-brand" />
          {STEPS.map(([t, d], i) => (
            <Reveal key={t} className="relative pb-14 pl-12 last:pb-0 sm:pb-20">
              <span className="absolute left-0 top-2 h-[15px] w-[15px] rounded-full border-2 border-brand bg-sink" />
              <p className="font-mono text-[11px] text-brand">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">{t}</h3>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-zinc-400">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Models = () => (
  <section data-testid="studio-models" className="border-t border-white/10 bg-sink py-24 sm:py-32 lg:py-40">
    <div className={wrap}>
      <div className="text-center">
        <Reveal><Eyebrow>Production Models</Eyebrow></Reveal>
        <Reveal delay={0.05}><H2 className="mt-6">One Video or <span className="text-brand">Fifty.</span></H2></Reveal>
        <Reveal delay={0.1}><p className="mx-auto mt-6 max-w-xl text-base text-zinc-400 sm:text-lg">Scale production around what your business needs.</p></Reveal>
      </div>
      <div className="mx-auto mt-16 grid max-w-5xl gap-5 md:grid-cols-2">
        {MODELS.map(([t, items], i) => (
          <Reveal key={t} delay={i * 0.08} className="rounded border border-white/10 bg-coal p-8 transition-colors duration-500 hover:border-brand/50 sm:p-12" data-testid={`studio-model-${i + 1}`}>
            <h3 className="font-display text-3xl font-bold text-white">{t}</h3>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">Ideal for</p>
            <ul className="mt-5 space-y-3">
              {items.map((it) => <li key={it} className="flex items-center gap-4 text-base text-zinc-200"><span className="h-px w-4 bg-brand" />{it}</li>)}
            </ul>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-14 flex justify-center"><Pill label="Discuss Your Video Requirements" variant="outline" testid="studio-models-cta" /></Reveal>
    </div>
  </section>
);
